import PlanChange from "../PlanChange";
import Model from "../Model";
import Person from "../Person";
import Income, {IncomeType} from "../Income";
import ModelStore from "../../stores/ModelStore";
import {CreateIncomeInput, UpdateIncomeInput} from "../../API";
import {
  createUUID, getDateToday,
  getISODateFromDate,
  getISODateTime,
  getISODateToday,
  isoToLocalDate
} from "../../stores/StoreUtilities";
import Schedule from "../Schedule";
import {addMonths, endOfMonth} from "date-fns";
import SocialSecurityCalc from "../../components/calculator/SocialSecurityCalc";

export enum SocialSecurityStrategy {
  MaximizeBenefits,
  MaximizeNetworth
}

export interface ISocialSecurityBenefit {
  personId: string
  fullBenefit: number
  personalStart?: string
  personalBenefit: number
  personalPercent?: number
  spousalStart?: string
  spousalBenefit: number
  spousalPercent?: number
  annualInf?: number
}

export interface ISocialSecurityStrategyChange {
  // Deprecated
  age1?: number
  startDate1?: string
  amount1?: number
  age2?: number
  startDate2?: string
  amount2?: number
  // New interface
  strategy?: SocialSecurityStrategy
  benefits?: ISocialSecurityBenefit[]
  applyReduction?: boolean
}

class SocialSecurityStrategyChange extends PlanChange implements ISocialSecurityStrategyChange {
  // Deprecated
  age1?: number
  startDate1?: string
  amount1?: number
  age2?: number
  startDate2?: string
  amount2?: number
  // New interface
  strategy?: SocialSecurityStrategy
  benefits?: ISocialSecurityBenefit[]
  applyReduction?: boolean

  constructor (data: any) {
    super(data)
    const items = this.details ? JSON.parse(this.details) : {}
    this.strategy = items.strategy ?? SocialSecurityStrategy.MaximizeBenefits
    if (items.benefits) {
      this.benefits = items.benefits
    } else {
      this.benefits = []
      if (items.startDate1) {
        this.benefits.push({
          personId: "",
          fullBenefit: 0,
          personalStart: items.startDate1,
          personalBenefit: items.amount1,
          spousalStart: "",
          spousalBenefit: 0
        })
      }
      if (items.startDate2) {
        this.benefits.push({
          personId: "",
          fullBenefit: 0,
          personalStart: items.startDate2,
          personalBenefit: items.amount2,
          spousalStart: "",
          spousalBenefit: 0
        })
      }
    }
    // this.age1 = items.age1
    // this.startDate1 = items.startDate1
    // this.amount1 = items.amount1
    // this.age2 = items.age2
    // this.startDate2 = items.startDate2
    // this.amount2 = items.amount2
    this.applyReduction = items.applyReduction
  }

  static reductionSchedule = new Map<number, number>([[2034, -0.19]])

  tempCreatedAt = "2199-12-31"

  // A spousal benefit cannot begin before the other spouse has filed for their own
  // benefit. Strategies saved before this rule was enforced may violate it; the
  // projection must not, so the saved benefit is corrected here when it is applied.
  effectiveBenefit(benefit: ISocialSecurityBenefit, index: number, model: Model): ISocialSecurityBenefit {
    const other = this.benefits && this.benefits.length > 1 ? this.benefits[index === 0 ? 1 : 0] : undefined
    // Legacy rows use "" rather than undefined for missing dates
    const otherFiled = other ? (other.personalStart || other.spousalStart || undefined) : undefined
    if (!benefit.spousalStart || !otherFiled || benefit.spousalStart >= otherFiled) {
      return benefit
    }

    const person = (index === 0) ? model.person1 : model.person2
    const otherPerson = (index === 0) ? model.person2 : model.person1
    const chosenStart = benefit.personalStart || benefit.spousalStart
    if (person && otherPerson && other && other.fullBenefit > 0 && chosenStart) {
      // The saved details include the PIAs, so the deferred benefit can be recomputed
      // (a deferral can change the early-claiming reduction, not just the start date)
      const recomputed = SocialSecurityCalc.calculateRetirementBenefits(
        isoToLocalDate(person.birthDate), isoToLocalDate(chosenStart), benefit.fullBenefit,
        isoToLocalDate(otherPerson.birthDate), isoToLocalDate(otherFiled), other.fullBenefit)
      return {
        ...benefit,
        personalStart: recomputed.myStart ? getISODateFromDate(recomputed.myStart) : undefined,
        personalBenefit: recomputed.myStart ? recomputed.myBenefit : 0,
        personalPercent: recomputed.myStart ? recomputed.myPercent : 0,
        spousalStart: recomputed.spousalStart ? getISODateFromDate(recomputed.spousalStart) : undefined,
        spousalBenefit: recomputed.spousalStart ? recomputed.spousalBenefit : 0,
        spousalPercent: recomputed.spousalStart ? recomputed.spousalPercent : 0
      }
    }

    // Without the PIAs only the start date can be corrected. Spousal benefits are also
    // never available before the claimant is 62, matching the floor in
    // SocialSecurityCalc.calculateRetirementBenefits. If the deferred spousal start no
    // longer follows the personal start, only the spousal benefit remains (otherwise
    // the personal income would end before it starts).
    let deferredStart = otherFiled
    if (person && person.birthDate) {
      const earliest62 = getISODateFromDate(addMonths(isoToLocalDate(person.birthDate), 62 * 12))
      if (deferredStart < earliest62) {
        deferredStart = earliest62
      }
    }
    const spousalOnly = !benefit.personalStart || deferredStart <= benefit.personalStart
    return {
      ...benefit,
      spousalStart: deferredStart,
      personalStart: spousalOnly ? undefined : benefit.personalStart,
      personalBenefit: spousalOnly ? 0 : benefit.personalBenefit
    }
  }

  apply(model: Model) {
    if (!this.benefits) {
      return
    }
    /*  incomes   personalBenefit   spousalBenefit
           0              -1               -1
           1               0               -1
           1              -1                0
           2               0                1
     */
    this.benefits.forEach((savedBenefit: ISocialSecurityBenefit, index: number) => {
      const person = (index === 0) ? model.person1 : model.person2
      if (person) {
        const benefit = this.effectiveBenefit(savedBenefit, index, model)
        let personalIndex = -1
        let spousalIndex = -1
        const incomes = this.getSocialSecurityIncomes(model, person)
        if (incomes.length === 1) {
          if ((benefit.personalStart && benefit.personalBenefit) || !benefit.spousalStart) {
            personalIndex = 0
          } else if (benefit.spousalStart && benefit.spousalBenefit) {
            spousalIndex = 0
          }
        } else if (incomes.length > 1) {
          personalIndex = 0
          spousalIndex = 1
        }

        if (personalIndex >= 0) {
          const income = incomes[personalIndex]
          income.updatedAt = getISODateTime()
          income.description = `${person.nickname} Social Security Benefit`
          income.start = benefit.personalStart ?? ""
          income.amount = benefit.personalBenefit ?? 0
          income.amountDate = getDateToday()
          income.end = benefit.spousalStart ? this.priorMonth(benefit.spousalStart) : ""
          income.annualInf = benefit.annualInf ?? 0
          income.annualInfSchedule = this.applyReduction ? SocialSecurityStrategyChange.reductionSchedule : undefined
          model.addLock(income.id, "*", "Locked by Social Security Strategy")
        } else if (benefit.personalStart && benefit.personalBenefit) {
          const income1 = new Income({
            id: createUUID(),
            createdAt: this.tempCreatedAt,
            updatedAt: getISODateTime(),
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            model: model,
            description: `${person.nickname} Social Security Benefit`,
            amount: benefit.personalBenefit,
            amountDate: getISODateToday(),
            incomeType: IncomeType.SocialSecurityBenefits,
            schedule: Schedule.getDefaultSchedule("Monthly"),
            start: benefit.personalStart,
            end: (benefit.spousalStart) ? this.priorMonth(benefit.spousalStart) : null,
            annualInf: benefit.annualInf ?? 0,
            annualInfSchedule: this.applyReduction ? SocialSecurityStrategyChange.reductionSchedule : null,
            infLock: false,
            survivorPercent: null,
            ownerId: person.id,
            owner: person,
            sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length-1].sortOrder + ModelStore.orderInterval : 0
          })
          model.incomes.push(income1)
          model.addLock(income1.id, "*", "Locked by Social Security Strategy")
        }

        if (spousalIndex >= 0) {
          const income = incomes[spousalIndex]
          income.updatedAt = getISODateTime()
          income.description = `${person.nickname} Spousal Social Security Benefit`
          income.start = benefit.spousalStart ?? ""
          income.amount = benefit.spousalBenefit ?? 0
          income.amountDate = getDateToday()
          income.end = ""
          income.annualInf = benefit.annualInf ?? 0
          income.annualInfSchedule = this.applyReduction ? SocialSecurityStrategyChange.reductionSchedule : undefined
          model.addLock(income.id, "*", "Locked by Social Security Strategy")
        } else if (benefit.spousalStart && benefit.spousalBenefit) {
          // Add a spousal benefit Income
          const income2 = new Income({
            id: createUUID(),
            createdAt: this.tempCreatedAt,
            updatedAt: getISODateTime(),
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            model: model,
            description: `${person.nickname} Spousal Social Security Benefit`,
            amount: benefit.spousalBenefit,
            amountDate: getISODateToday(),
            incomeType: IncomeType.SocialSecurityBenefits,
            schedule: Schedule.getDefaultSchedule("Monthly"),
            start: benefit.spousalStart,
            end: null,
            annualInf: benefit.annualInf ?? 0,
            annualInfSchedule: this.applyReduction ? SocialSecurityStrategyChange.reductionSchedule : null,
            infLock: false,
            survivorPercent: null,
            ownerId: person.id,
            owner: person,
            sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length-1].sortOrder + ModelStore.orderInterval : 0
          })
          model.incomes.push(income2)
          model.addLock(income2.id, "*", "Locked by Social Security Strategy")
        }

        if (incomes.length > 2) {
          // Disable additional social security incomes
          for (let i = 2; i < incomes.length; i++) {
            const income = incomes[i]
            income.start = ""
            income.amount = 0
            income.amountDate = getDateToday()
            income.end = ""
            income.annualInf = 0
            model.addLock(income.id, "*", "Locked by Social Security Strategy")
          }
        }
      }
    })

    Model.sortIncomes(model.incomes)
  }

  getSocialSecurityIncomes(model: Model, person: Person): Income[] {
    const incomes = model.incomes.filter((i: Income) => i.incomeType === IncomeType.SocialSecurityBenefits && i.ownerId === person.id && !i.archived)
    return incomes
  }

  priorMonth(isoDate: string) {
    const date = endOfMonth(addMonths(isoToLocalDate(isoDate), -1))
    return getISODateFromDate(date)
  }

  async commit(model: Model, modelStore: ModelStore) {
    if (!this.benefits) {
      return
    }

    let index = 0
    for (const savedBenefit of this.benefits) {
      const person = (index === 0) ? model.person1 : model.person2
      if (person) {
        const benefit = this.effectiveBenefit(savedBenefit, index, model)
        let personalIndex = -1
        let spousalIndex = -1
        const incomesTemp = this.getSocialSecurityIncomes(model, person)
        const incomes: Income[] = []
        // clear out temporary incomes
        for (let income of incomesTemp) {
          if (income.createdAt === this.tempCreatedAt) {
            const i = model.incomes.findIndex((inc: Income) => inc.id === income.id)
            if (i >= 0) {
              model.incomes.splice(i, 1)
            }
          } else {
            incomes.push(income)
          }
        }

        if (incomes.length === 1) {
          if ((benefit.personalStart && benefit.personalBenefit) || !benefit.spousalStart) {
            personalIndex = 0
          } else if (benefit.spousalStart && benefit.spousalBenefit) {
            spousalIndex = 0
          }
        } else if (incomes.length > 1) {
          personalIndex = 0
          spousalIndex = 1
        }

        if (personalIndex >= 0) {
          const income = incomes[personalIndex]
          if (benefit.personalStart && benefit.personalBenefit) {
            const update: UpdateIncomeInput = {
              id: income.id,
              accountId: income.accountId,
              description: `${person.nickname} Social Security Benefit`,
              amount: benefit.personalBenefit,
              amountDate: getISODateToday(),
              start: benefit.personalStart,
              end: benefit.spousalStart ? this.priorMonth(benefit.spousalStart) : null,
              annualInf: benefit.annualInf ?? 0
            }
            await modelStore.updateIncome(update)
          } else {
            // Delete it
            await modelStore.deleteIncome(income.id)
          }
          model.deleteLock(income.id, "*")
        } else if (benefit.personalStart && benefit.personalBenefit > 0) {
          // Create social security income
          const input: CreateIncomeInput = {
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            description: `${person.nickname} Social Security Benefit`,
            amount: benefit.personalBenefit,
            amountDate: getISODateToday(),
            incomeType: IncomeType.SocialSecurityBenefits,
            schedule: JSON.stringify(Schedule.getDefaultSchedule("Monthly")),
            start: benefit.personalStart,
            end: (benefit.spousalStart) ? this.priorMonth(benefit.spousalStart) : null,
            annualInf: benefit.annualInf ?? 0,
            infLock: false,
            survivorPercent: null,
            ownerId: person.id,
            sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length-1].sortOrder + ModelStore.orderInterval : 0
          }
          await modelStore.createIncome(input)
        }

        if (spousalIndex >= 0) {
          const income = incomes[spousalIndex]
          if (benefit.spousalStart && benefit.spousalBenefit) {
            const update: UpdateIncomeInput = {
              id: income.id,
              accountId: income.accountId,
              description: `${person.nickname} Spousal Social Security Benefit`,
              amount: benefit.spousalBenefit,
              amountDate: getISODateToday(),
              start: benefit.spousalStart,
              end: null,
              annualInf: benefit.annualInf ?? 0
            }
            await modelStore.updateIncome(update)
          } else {
            // Delete it
            await modelStore.deleteIncome(income.id)
          }
          model.deleteLock(income.id, "*")
        } else if (benefit.spousalStart && benefit.spousalBenefit) {
          // Add a spousal benefit Income
          const input: CreateIncomeInput = {
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            description: `${person.nickname} Spousal Social Security Benefit`,
            amount: benefit.spousalBenefit,
            amountDate: getISODateToday(),
            incomeType: IncomeType.SocialSecurityBenefits,
            schedule: JSON.stringify(Schedule.getDefaultSchedule("Monthly")),
            start: benefit.spousalStart,
            end: null,
            annualInf: benefit.annualInf ?? 0,
            infLock: false,
            survivorPercent: null,
            ownerId: person.id,
            sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length - 1].sortOrder + ModelStore.orderInterval : 0
          }
          await modelStore.createIncome(input)
        }

        if (incomes.length > 2) {
          // Delete additional social security incomes
          for (let i = 2; i < incomes.length; i++) {
            await modelStore.deleteIncome(incomes[i].id)
          }
        }
      }
      ++index
    }
    Model.sortIncomes(model.incomes)
  }
}

export default SocialSecurityStrategyChange